import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetRecordingUrlApiV1CallsCallIdRecordingGet } from '#/shared/api/generated/communication/theCargoCommunication'
import { cn } from '#/shared/lib/utils'
import { PauseIcon, PlayFilledIcon } from '#/shared/ui/icons'

import { useRecordingPlayer } from '../../model/use-recording-player'

interface CallRecordingAudioProps {
  /** Source `calls` row id used to fetch the time-limited recording URL. */
  callId: string
  /** Known talk time in seconds, shown as the total until audio metadata loads. */
  durationSeconds?: number
}

/** Seconds → `m:ss`. */
function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds))
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`
}

/** Round play/pause control. */
function PlayButton({
  playing,
  onToggle,
}: {
  playing: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={t(
        playing
          ? 'phonePanel.conversation.pause'
          : 'phonePanel.conversation.play',
      )}
      className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-xl bg-blue-500 text-paper transition hover:brightness-95"
    >
      {playing ? (
        <PauseIcon className="size-3.5" />
      ) : (
        // Right-pointing triangle is optically left-heavy — nudge it right so
        // it reads as centered in the round button.
        <PlayFilledIcon className="size-3 translate-x-px" />
      )}
    </button>
  )
}

/** Scrub bar: click or drag anywhere along the track to seek. The rail rests at
 *  3px (per the design) and grows on hover/drag for an easier grab target; a
 *  thumb marks the playhead, and the cursor reads grab → grabbing while held. */
function SeekBar({
  progress,
  onSeek,
}: {
  progress: number
  onSeek: (fraction: number) => void
}) {
  const { t } = useTranslation()
  const trackRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState(false)

  const seekFromClientX = (clientX: number) => {
    const track = trackRef.current
    if (!track) return
    const rect = track.getBoundingClientRect()
    onSeek((clientX - rect.left) / rect.width)
  }

  // Rail thickens on hover, and stays thick while dragging even if the pointer
  // leaves the element.
  const rail = cn(
    'rounded-sm transition-[height]',
    dragging ? 'h-1.5' : 'h-0.75 group-hover:h-1.5',
  )

  return (
    <div
      ref={trackRef}
      role="slider"
      tabIndex={0}
      aria-label={t('phonePanel.conversation.seek')}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress)}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId)
        setDragging(true)
        seekFromClientX(event.clientX)
      }}
      onPointerMove={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          seekFromClientX(event.clientX)
        }
      }}
      onPointerUp={() => setDragging(false)}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') onSeek((progress - 5) / 100)
        else if (event.key === 'ArrowRight') onSeek((progress + 5) / 100)
      }}
      className={cn(
        'group relative flex min-w-0 flex-1 touch-none items-center py-2',
        dragging ? 'cursor-grabbing' : 'cursor-grab',
      )}
    >
      <div
        className={cn(rail, 'bg-blue-500')}
        style={{ width: `${progress}%` }}
      />
      <div className={cn(rail, 'min-w-px flex-1 bg-gray-200')} />
      <div
        className={cn(
          'absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500 ring-2 ring-paper transition-transform',
          'top-1/2 group-hover:scale-110',
          dragging && 'scale-110',
        )}
        style={{ left: `${progress}%` }}
      />
    </div>
  )
}

/** Tap to cycle 1× → 1.5× → 2× and back. */
function SpeedButton({
  speed,
  onCycle,
}: {
  speed: number
  onCycle: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onCycle}
      aria-label={t('phonePanel.conversation.playbackSpeed')}
      className="shrink-0 cursor-pointer text-xs font-normal text-gray-900 tabular-nums transition hover:text-blue-500"
    >
      {speed}X
    </button>
  )
}

/** The actual play/pause + scrub bar + speed control once a URL is resolved. */
function PlayerBar({
  url,
  durationSeconds,
}: {
  url: string
  durationSeconds: number
}) {
  const {
    audioRef,
    playing,
    setPlaying,
    current,
    total,
    setTotal,
    setCurrent,
    progress,
    speed,
    toggle,
    seekToFraction,
    cycleSpeed,
  } = useRecordingPlayer(durationSeconds)

  return (
    <div className="flex items-center gap-2.5">
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        // Keeps the bar in sync when paused/seeked; playback uses rAF above.
        onTimeUpdate={(event) => setCurrent(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => {
          const seconds = event.currentTarget.duration
          if (Number.isFinite(seconds)) setTotal(seconds)
        }}
      />
      <PlayButton playing={playing} onToggle={toggle} />
      <SeekBar progress={progress} onSeek={seekToFraction} />
      <span className="shrink-0 text-xs whitespace-nowrap text-warm-500 tabular-nums">
        {clock(current)} / {clock(total)}
      </span>
      <SpeedButton speed={speed} onCycle={cycleSpeed} />
    </div>
  )
}

/**
 * Recording player for a call card: lazily fetches the call's recording URL
 * (mounts only when the card is expanded) with no retry, so a single response
 * decides — loading → player → or a "no recording" notice — without flashing a
 * fake player first.
 */
export function CallRecordingAudio({
  callId,
  durationSeconds = 0,
}: CallRecordingAudioProps) {
  const { t } = useTranslation()
  const { data, isLoading, isError } =
    useGetRecordingUrlApiV1CallsCallIdRecordingGet(callId, undefined, {
      query: { retry: false },
    })
  const url = data?.data.recordings?.[0]?.url

  if (isLoading) {
    return (
      <span className="text-xs text-warm-500">
        {t('phonePanel.conversation.loadingRecording')}
      </span>
    )
  }
  // First response settled with no recording (empty list or error).
  if (isError || !url) {
    return (
      <span className="text-xs text-warm-500">
        {t('phonePanel.conversation.noRecording')}
      </span>
    )
  }

  return <PlayerBar url={url} durationSeconds={durationSeconds} />
}
